/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatDate, formatMoney } from "../utils/utils";

const normalizarRutBusqueda = (value) => String(value || "").toLowerCase().replace(/[^0-9k]/g, "");
const normalizarValor = (value) => Number(String(value || "").replace(/[^0-9-]/g, "")) || 0;
const tieneValorCsv = (row) => row.valor_actual_csv !== undefined && row.valor_actual_csv !== null && row.valor_actual_csv !== "";
const valorCoincide = (row) => tieneValorCsv(row) && normalizarValor(row.valor_actual_csv) === Number(row.valor_sistema_turno || 0);
const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const ReajusteValoresFamiliaImport = () => {
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [busqueda, setBusqueda] = useState("");
    const [filtroComparacion, setFiltroComparacion] = useState("todos");

    const importar = async (dryRun = true) => {
        if (!archivo) {
            gdAlertify.notiError("Seleccione un archivo CSV.");
            return;
        }

        const formData = new FormData();
        formData.append("file", archivo);
        formData.append("dryRun", dryRun ? "true" : "false");

        setLoading(true);
        try {
            const resp = await axiosInstance.post("/paciente/reajuste-familia/importar-historico", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (resp.data?.error) {
                throw new Error(resp.data.message || "No se pudo procesar el archivo.");
            }

            setResultado(resp.data.data);
            gdAlertify.notiSuccess(resp.data.message || "Archivo procesado.");
        } catch (error) {
            gdAlertify.notiError(error.response?.data?.message || error.message || "Error al importar.");
        } finally {
            setLoading(false);
        }
    };

    const confirmarAplicacion = () => {
        gdAlertify.confirm({
            title: "Aplicar importacion historica",
            message: "Esto actualizara ultimo/proximo reajuste familia e insertara una observacion historica por paciente encontrado.",
            onConfirm: () => importar(false),
            onCancel: () => {}
        });
    };

    const detalle = resultado?.detalle || [];
    const detalleFiltrado = detalle.filter((row) => {
        if (filtroComparacion === "diferentes" && (!tieneValorCsv(row) || valorCoincide(row))) return false;
        if (filtroComparacion === "coinciden" && !valorCoincide(row)) return false;

        const q = busqueda.trim().toLowerCase();
        if (!q) return true;
        const rutQuery = normalizarRutBusqueda(q);
        const rutRow = normalizarRutBusqueda(row.rut);
        if (rutQuery && rutRow.includes(rutQuery)) return true;

        return [
            row.rut,
            row.nombre_csv,
            row.nombre_paciente,
            row.estado,
            row.motivo,
        ].some((value) => String(value || "").toLowerCase().includes(q));
    });

    const renderEstado = (estado) => {
        const encontrado = estado === "previsualizado" || estado === "actualizado";
        if (encontrado) return <span className="badge bg-success">Encontrado</span>;
        if (estado === "no_encontrado") return <span className="badge bg-warning text-dark">No encontrado</span>;
        if (estado === "sin_valores_servicio") return <span className="badge bg-info text-dark">Sin valores</span>;
        return <span className="badge bg-danger">Error</span>;
    };

    const exportarFiltrados = () => {
        if (detalleFiltrado.length === 0) {
            gdAlertify.notiError("No hay filas para exportar.");
            return;
        }

        const headers = [
            "Estado",
            "RUT",
            "Nombre CSV",
            "Paciente sistema",
            "Valor sistema turno",
            "Valor CSV",
            "Comparacion",
            "Valor sistema base",
            "Valor sistema colacion",
            "Valor sistema locomocion",
            "Valor sistema otro",
            "Ultimo reajuste",
            "Proximo reajuste",
            "Detalle",
        ];

        const rows = detalleFiltrado.map((row) => [
            row.estado,
            row.rut,
            row.nombre_csv,
            row.nombre_paciente,
            row.valor_sistema_turno ?? "",
            row.valor_actual_csv ?? "",
            tieneValorCsv(row) ? (valorCoincide(row) ? "Coincide" : "Diferente") : "",
            row.valor_sistema_base ?? "",
            row.valor_sistema_colacion ?? "",
            row.valor_sistema_locomocion ?? "",
            row.valor_sistema_otro ?? "",
            row.ultimo_reajuste_familia ?? "",
            row.proximo_reajuste_familia ?? "",
            row.motivo || row.observacion || "",
        ]);

        const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(";")).join("\r\n");
        const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `reajustes-familia-${filtroComparacion}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const renderComparacionValor = (row) => {
        const valorCsv = normalizarValor(row.valor_actual_csv);
        const valorSistema = Number(row.valor_sistema_turno || 0);
        const tieneComparacion = tieneValorCsv(row);
        const coincide = tieneComparacion && valorCsv === valorSistema;

        return (
            <div style={{ minWidth: 170 }}>
                <div className="d-flex justify-content-between gap-2">
                    <span className="text-muted">Sistema</span>
                    <strong>${formatMoney(valorSistema)}</strong>
                </div>
                <div className="d-flex justify-content-between gap-2">
                    <span className="text-muted">CSV</span>
                    <strong>{row.valor_actual_csv || "-"}</strong>
                </div>
                {tieneComparacion && (
                    <span className={`badge ${coincide ? "bg-success" : "bg-warning text-dark"} mt-1`}>
                        {coincide ? "Coincide" : "Diferente"}
                    </span>
                )}
                <div className="text-muted small mt-1">
                    Base ${formatMoney(row.valor_sistema_base || 0)} · Col ${formatMoney(row.valor_sistema_colacion || 0)} · Mov ${formatMoney(row.valor_sistema_locomocion || 0)} · Otro ${formatMoney(row.valor_sistema_otro || 0)}
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <h3 className="mb-1">Importar reajustes familia historicos</h3>
                    <div className="text-muted small mb-3">Ruta oculta para cargar CSV historico por RUT.</div>

                    <div className="row g-3 align-items-end">
                        <div className="col-md-6">
                            <label className="form-label">Archivo CSV</label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".csv,text/csv"
                                onChange={(e) => {
                                    setArchivo(e.target.files?.[0] || null);
                                    setResultado(null);
                                }}
                            />
                        </div>
                        <div className="col-md-3">
                            <button className="btn btn-outline-primary w-100" onClick={() => importar(true)} disabled={loading || !archivo}>
                                {loading ? "Procesando..." : "Previsualizar"}
                            </button>
                        </div>
                        <div className="col-md-3">
                            <button className="btn btn-danger w-100" onClick={confirmarAplicacion} disabled={loading || !archivo}>
                                Aplicar importacion
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {resultado && (
                <div className="card mt-3">
                    <div className="card-body">
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <span className="badge bg-secondary">Leidos: {resultado.leidos}</span>
                            <span className="badge bg-success">{resultado.dryRun ? "Previsualizados" : "Actualizados"}: {resultado.actualizados}</span>
                            <span className="badge bg-warning text-dark">No encontrados: {resultado.no_encontrados}</span>
                            <span className="badge bg-info text-dark">Sin valores: {resultado.sin_valores}</span>
                            <span className="badge bg-danger">Errores: {resultado.errores}</span>
                        </div>

                        <div className="row g-2 align-items-end mb-3">
                            <div className="col-md-5">
                                <label className="form-label">Buscar paciente</label>
                                <input
                                    className="form-control"
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    placeholder="RUT, nombre CSV, paciente o estado"
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Comparacion</label>
                                <select
                                    className="form-select"
                                    value={filtroComparacion}
                                    onChange={(e) => setFiltroComparacion(e.target.value)}
                                >
                                    <option value="todos">Todos</option>
                                    <option value="diferentes">Solo diferentes</option>
                                    <option value="coinciden">Solo coincidentes</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <div className="text-muted small">
                                    Mostrando {detalleFiltrado.length} de {detalle.length}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success mt-2"
                                    onClick={exportarFiltrados}
                                    disabled={detalleFiltrado.length === 0}
                                >
                                    Exportar filtrados
                                </button>
                            </div>
                        </div>

                        <div className="table-responsive" style={{ maxHeight: 620 }}>
                            <table className="table table-sm table-bordered align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Estado</th>
                                        <th>RUT</th>
                                        <th>CSV</th>
                                        <th>Paciente</th>
                                        <th>Valor sistema / CSV</th>
                                        <th>Ultimo</th>
                                        <th>Proximo</th>
                                        <th>Detalle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalleFiltrado.map((row, index) => (
                                        <tr key={`${row.rut || "row"}-${index}`}>
                                            <td>
                                                {renderEstado(row.estado)}
                                                <div className="text-muted small mt-1">{row.estado}</div>
                                            </td>
                                            <td>{row.rut}</td>
                                            <td>{row.nombre_csv || "-"}</td>
                                            <td>{row.nombre_paciente || "-"}</td>
                                            <td>{renderComparacionValor(row)}</td>
                                            <td>{row.ultimo_reajuste_familia ? formatDate(row.ultimo_reajuste_familia) : "-"}</td>
                                            <td>{row.proximo_reajuste_familia ? formatDate(row.proximo_reajuste_familia) : "-"}</td>
                                            <td className="small">{row.motivo || row.observacion || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReajusteValoresFamiliaImport;
